import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getLanguage } from '../utils/i18n';

// Cache za search queries (max 20)
const searchCache = new Map();
const MAX_CACHE_SIZE = 20;
let nominatimWarned = false;

// Translate city names based on current language
const translateCityName = (cityName: string): string => {
  const lang = getLanguage();
  if (lang === 'sr' || !cityName) return cityName;
  
  // Common Serbian city names translations
  const translations: Record<string, Record<string, string>> = {
    'en': {
      'Beograd': 'Belgrade',
      'Novi Sad': 'Novi Sad',
      'Niš': 'Nis',
      'Kragujevac': 'Kragujevac',
      'Subotica': 'Subotica',
      'Zrenjanin': 'Zrenjanin',
      'Pančevo': 'Pancevo',
      'Čačak': 'Cacak',
      'Kraljevo': 'Kraljevo',
      'Smederevo': 'Smederevo',
    }
  };
  
  return translations[lang]?.[cityName] || cityName;
};

// Prevedi nazive na srpski
const translateToSerbian = (text: string): string => {
  const translations: Record<string, string> = {
    'Serbia': 'Srbija',
    'Belgrade': 'Beograd',
    'Novi Sad': 'Novi Sad',
    'Nis': 'Niš',
    'Kragujevac': 'Kragujevac',
    'Subotica': 'Subotica',
    'Zrenjanin': 'Zrenjanin',
    'Pancevo': 'Pančevo',
    'Cacak': 'Čačak',
    'Kraljevo': 'Kraljevo',
    'Smederevo': 'Smederevo',
  };
  return translations[text] || text;
};

const getApiBase = () => (
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' ? `${window.location.origin}/api` : 'http://localhost:5000/api')
);

// Geocoding via backend proxy (/api/geocode/*) with caching + AbortController
const searchLocation = async (query: string, signal?: AbortSignal) => {
  if (!query || query.length < 3) return [];
  
  // Proveri cache
  const cacheKey = query.toLowerCase().trim();
  if (searchCache.has(cacheKey)) {
    return searchCache.get(cacheKey);
  }
  
  try {
    const lang = getLanguage();
    const acceptLang = lang === 'sr' ? 'sr' : 'en';
    const url = `${getApiBase()}/geocode/search?limit=10&countrycodes=rs&lang=${encodeURIComponent(acceptLang)}&q=${encodeURIComponent(query)}`;
    const response = await fetch(url, {
      headers: {
        'Accept-Language': acceptLang
      },
      signal
    });
    
    if (!response.ok) return [];
    
    const data = await response.json();
    
    // Map i formatiraj rezultate
    const resultsMap = new Map<string, any>();
    
    data.forEach((item: any) => {
      // Get city/municipality name
      const cityName = item.address?.city || item.address?.town || item.address?.municipality || '';
      const municipality = item.address?.municipality || '';
      const suburb = item.address?.suburb || item.address?.quarter || '';
      const district = item.address?.district || '';
      
      // Konstruiši bolji label za srpski
      let displayLabel = item.display_name;
      if (lang === 'sr') {
        const addressParts: string[] = [];
        
        // Za opštine/municipalities (npr. Zvezdara)
        if (municipality && !suburb) {
          addressParts.push(municipality);
          if (cityName && cityName !== municipality) addressParts.push(cityName);
        } else if (suburb) {
          // Suburb/quarter (npr. Zvezdara)
          addressParts.push(suburb);
          if (municipality && municipality !== suburb) addressParts.push(municipality);
          if (cityName && cityName !== municipality && cityName !== suburb) addressParts.push(cityName);
        } else if (district) {
          addressParts.push(district);
          if (cityName && cityName !== district) addressParts.push(cityName);
        } else if (item.address?.road) {
          // Ulica
          addressParts.push(item.address.road);
          if (item.address.house_number) addressParts.push(item.address.house_number);
          if (cityName) addressParts.push(cityName);
        } else if (cityName) {
          addressParts.push(cityName);
        }
        
        // Dodaj državu
        if (item.address?.country) {
          const country = translateToSerbian(item.address.country);
          addressParts.push(country);
        }
        
        if (addressParts.length > 0) {
          displayLabel = addressParts.join(', ');
        } else {
          // Fallback: prevedi display_name
          displayLabel = item.display_name
            .replace(/Serbia/g, 'Srbija')
            .replace(/Belgrade/g, 'Beograd');
        }
      } else {
        // Za engleski, samo prevedi državu ako je potrebno
        displayLabel = item.display_name;
      }
      
      // Prevedi city name
      let translatedCity = cityName || municipality || suburb || district;
      if (lang === 'sr' && translatedCity) {
        translatedCity = translateToSerbian(translatedCity);
      }
      
      // Koristi label kao ključ za deduplikaciju (zaokruži koordinate na 4 decimale)
      const coordKey = `${Math.round(parseFloat(item.lat) * 10000) / 10000},${Math.round(parseFloat(item.lon) * 10000) / 10000}`;
      const uniqueKey = `${displayLabel}|${coordKey}`;
      
      if (!resultsMap.has(uniqueKey)) {
        resultsMap.set(uniqueKey, {
          label: displayLabel,
          city: translatedCity || cityName || municipality || suburb || district,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
          type: item.type,
          address: item.address
        });
      }
    });
    
    const results = Array.from(resultsMap.values());
    
    // Dodaj u cache (LRU)
    if (searchCache.size >= MAX_CACHE_SIZE) {
      const firstKey = searchCache.keys().next().value;
      searchCache.delete(firstKey);
    }
    searchCache.set(cacheKey, results);
    
    return results;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      return []; // Request cancelled, ignore
    }
    // Nominatim can be blocked by network/adblock; don't spam console
    if (!nominatimWarned) {
      nominatimWarned = true;
      console.warn('Location search temporarily unavailable (geocoding blocked).');
    }
    return [];
  }
};

export interface LocationSuggestion {
  label: string;
  city: string;
  lat: number;
  lon: number;
  type?: string;
  address?: any;
}

export function useLocationSearch() {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  const search = useCallback(async (query: string, debounceMs = 300) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Hide suggestions if query is too short
    if (!query || query.length < 3) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    // Debounce API call
    debounceTimerRef.current = setTimeout(async () => {
      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();
      
      try {
        const results = await searchLocation(query, abortControllerRef.current.signal);
        // Only update if request wasn't cancelled
        if (!abortControllerRef.current.signal.aborted) {
          setSuggestions(results);
          setIsLoading(false);
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          setIsLoading(false);
        }
      }
    }, debounceMs);
  }, []);
  
  const clear = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setSuggestions([]);
    setIsLoading(false);
  }, []);
  
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
  
  // Pretraga u blizini (2km radius oko trenutne lokacije)
  const searchNearby = useCallback(async (onSelect?: (location: LocationSuggestion) => void) => {
    if (!navigator.geolocation) {
      console.error('Geolocation not supported');
      return;
    }
    
    setIsLoading(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Reverse geocoding da dobijemo ime lokacije
          const lang = getLanguage();
          const acceptLang = lang === 'sr' ? 'sr' : 'en';
          const reverseUrl = `${getApiBase()}/geocode/reverse?lat=${latitude}&lon=${longitude}&lang=${encodeURIComponent(acceptLang)}&zoom=16`;
          
          const response = await fetch(reverseUrl, {
            headers: {
              'Accept-Language': acceptLang
            }
          });
          
          if (!response.ok) throw new Error('Reverse geocode failed');
          
          const data = await response.json();
          const addr = data.address || {};
          
          // Konstruiši label
          let label = data.display_name || '';
          if (lang === 'sr') {
            const parts: string[] = [];
            if (addr.suburb || addr.quarter) parts.push(addr.suburb || addr.quarter);
            if (addr.municipality && addr.municipality !== (addr.suburb || addr.quarter)) parts.push(addr.municipality);
            if (addr.city && addr.city !== addr.municipality) parts.push(addr.city);
            if (addr.country) parts.push(translateToSerbian(addr.country));
            if (parts.length > 0) label = parts.join(', ');
          }
          
          const city = addr.city || addr.municipality || addr.suburb || addr.quarter || '';
          const translatedCity = lang === 'sr' ? translateToSerbian(city) : city;
          
          const location: LocationSuggestion = {
            label: label || `Blizina (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
            city: translatedCity || city,
            lat: latitude,
            lon: longitude,
            type: 'nearby',
            address: addr
          };
          
          setIsLoading(false);
          
          if (onSelect) {
            onSelect(location);
          }
        } catch (error) {
          // If reverse geocoding is blocked, still return coords (no noisy error)
          if (!nominatimWarned) {
            nominatimWarned = true;
            console.warn('Reverse geocoding temporarily unavailable (blocked).');
          }
          const location: LocationSuggestion = {
            label: `Blizina (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
            city: '',
            lat: latitude,
            lon: longitude,
            type: 'nearby',
            address: {}
          };
          setIsLoading(false);
          onSelect?.(location);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, []);
  
  return {
    suggestions,
    isLoading,
    search,
    clear,
    searchNearby
  };
}

export function useSearchQueryState() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const getLocation = () => {
    return {
      query: searchParams.get('search') || searchParams.get('location') || '',
      lat: searchParams.get('lat') ? parseFloat(searchParams.get('lat')!) : null,
      lon: searchParams.get('lon') ? parseFloat(searchParams.get('lon')!) : null,
      mode: searchParams.get('mode') || 'offers',
    };
  };
  
  const setLocation = useCallback((location: { label: string; lat?: number; lon?: number }, mode?: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('search', location.label);
    if (location.lat) params.set('lat', location.lat.toString());
    if (location.lon) params.set('lon', location.lon.toString());
    if (mode) params.set('mode', mode);
    setSearchParams(params, { replace: false });
  }, [searchParams, setSearchParams]);
  
  const navigateToSearch = useCallback((location: { label: string; lat?: number; lon?: number }, mode: 'offers' | 'requests') => {
    const params = new URLSearchParams();
    params.set('search', location.label);
    if (location.lat) params.set('lat', location.lat.toString());
    if (location.lon) params.set('lon', location.lon.toString());
    params.set('mode', mode);
    navigate(`/dashboard/jobs?${params.toString()}`);
  }, [navigate]);
  
  return {
    getLocation,
    setLocation,
    navigateToSearch,
    searchParams,
    setSearchParams
  };
}
